// src/pages/salary/EgyptianSalaryPage.jsx
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DollarSign, Calculator, Shield, Award, TrendingUp, Calendar, Loader, Printer } from '../../constant/icons';
import ReusableDropdown from '../../components/ReusableDropdown';
import AlertModal       from '../../components/AlertModal';
import { ThemeContext }    from '../../constant/theme';
import { LanguageContext } from '../../constant/LanguageContext';

import { translations, getMonthName } from './config/translations_Salary';
import { getSalaryStyles, getHoverStyles, buildSalaryStats, buildStatsGridStyles } from './styles/salaryStyles';
import { usePayrollData, MISSING_SALARY_DATA } from './hooks/usePayrollData';
import StatsGrid          from '../../components/StatsGrid';
import InstancesTable     from './components/InstancesTable';
import Mo3tiCard          from './components/Mo3tiCard';
import DeductionsSection  from './components/DeductionsSection';
import AllowancesSection  from './components/AllowancesSection';
import SalaryComposition  from './components/SalaryComposition';
import PrintablePayslip   from './components/PrintablePayslip';
import MissingSalaryState from './components/MissingSalaryState';
import { exportPayslipPdf, payslipFileName } from './utils/payslipPdf';

const EgyptianSalaryPage = ({ apiBaseUrl = process.env.REACT_APP_BASE_URL }) => {
  const { theme }    = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isRTL        = language === 'ar';
  const t            = translations[language] || translations.en;

  // ── responsive ───────────────────────────────────────────────
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth, height: window.innerHeight,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024 && window.innerWidth <= 1440,
    isLargeDesktop: window.innerWidth > 1440,
  });

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setScreenSize({ width: w, height: window.innerHeight, isMobile: w <= 768, isTablet: w > 768 && w <= 1024, isDesktop: w > 1024 && w <= 1440, isLargeDesktop: w > 1440 });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── alert ────────────────────────────────────────────────────
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '', variant: 'info' });
  const showAlert  = (title, message, variant = 'info') => setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((p) => ({ ...p, open: false }));

  // ── data hook ────────────────────────────────────────────────
  const {
    infinityCode, clientCode, arabicName,
    loading, creating, error,
    payrollData, calculatedValues,
    selectedYear, setSelectedYear,
    selectedMonth, setSelectedMonth,
    availableMonths, availableYears,
  } = usePayrollData({ apiBaseUrl, t });

  // ── Notification deep-link (?year=&month=[&download=1]) ──────
  // Jumps to that payslip period. With download=1 ("salary paid"
  // notification) the payslip PDF is downloaded automatically once THAT
  // period's data has loaded. Keyed by the link itself (not a one-shot flag)
  // so clicking a second notification while already on this page still works.
  const [searchParams, setSearchParams] = useSearchParams();
  const lastDeepLinkRef = useRef('');
  const pendingDownloadRef = useRef(null); // { year, month } awaiting auto-download
  useEffect(() => {
    const year = Number(searchParams.get('year'));
    const month = Number(searchParams.get('month'));
    if (!year || !month) return;
    const wantsDownload = searchParams.get('download') === '1';
    const key = `${year}-${month}-${wantsDownload ? 'd' : ''}`;
    if (lastDeepLinkRef.current === key) return;
    lastDeepLinkRef.current = key;
    setSelectedYear(year);
    setSelectedMonth(month);
    if (wantsDownload) {
      pendingDownloadRef.current = { year, month };
      // Drop the flag from the URL so a refresh / back-navigation doesn't
      // download the PDF a second time.
      const next = new URLSearchParams(searchParams);
      next.delete('download');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── styles ───────────────────────────────────────────────────
  const styles = getSalaryStyles(theme, isRTL, screenSize);

  // Build stats from extracted functions in salaryStyles.js
  const icons = { DollarSign, Calculator, Shield, Award };
  const stats = buildSalaryStats(payrollData, calculatedValues, t, icons);
  const statsGridStyles = buildStatsGridStyles(styles, theme, isRTL, screenSize);

  const handleMouseEnter = (e) => {
    if (!screenSize.isMobile) Object.assign(e.currentTarget.style, getHoverStyles(theme, false));
  };
  const handleMouseLeave = (e) => {
    if (!screenSize.isMobile) {
      e.currentTarget.style.transform   = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow   = theme === 'dark' ? '0 8px 32px rgba(0,0,0,0.2)' : '0 8px 32px rgba(0,0,0,0.08)';
      e.currentTarget.style.background  = theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)';
      e.currentTarget.style.border      = theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)';
    }
  };

  // ── PDF download ─────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    const el = document.querySelector('.printable-payslip');
    if (!el) { showAlert('Error', language === 'ar' ? 'لم يتم العثور على كشف الراتب' : 'Payslip not found', 'error'); return; }
    try {
      const name = payrollData?.employeeName || arabicName || infinityCode || 'employee';
      await exportPayslipPdf(el, { isRTL, fileName: payslipFileName(name, getMonthName(selectedMonth, t), selectedYear) });
    } catch (err) {
      console.error('PDF failed', err);
      showAlert('Error', language === 'ar' ? 'فشل في إنشاء PDF' : 'Failed to generate PDF', 'error');
    }
  };

  // Fallback auto-download (?download=1): the "salary paid" notification
  // normally downloads the PDF itself BEFORE navigating here; it only adds
  // download=1 when that failed, so the page retries once the right period
  // has loaded (never the month shown before the jump).
  useEffect(() => {
    const target = pendingDownloadRef.current;
    if (!target || loading || !payrollData) return;
    if (Number(payrollData.year) !== target.year || Number(payrollData.month) !== target.month) return;
    pendingDownloadRef.current = null;
    // Two frames: let PrintablePayslip render the new period before capture.
    requestAnimationFrame(() => requestAnimationFrame(() => handleDownloadPDF()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, payrollData]);

  // ── loading / error / empty states ───────────────────────────
  if (loading) {
    const isDark = theme === 'dark';
    const shimmerBg = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)';
    const shimmerGrad = isDark 
      ? 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.06) 50%, rgba(0,0,0,0) 100%)';
    
    return (
      <div style={{ ...styles.container, marginTop: "-30px", marginBottom: "20px" }}>
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes pulseText {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
          @keyframes spin { 
            from { transform: rotate(0deg); } 
            to { transform: rotate(360deg); } 
          }
          .skeleton-box {
            position: relative;
            overflow: hidden;
            background-color: ${shimmerBg};
            border-radius: 12px;
          }
          .skeleton-box::after {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background-image: ${shimmerGrad};
            animation: shimmer 2s infinite ease-in-out;
          }
        `}</style>
        
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px', position: 'relative', zIndex: 1, padding: screenSize.isMobile ? '10px' : '0' }}>
          
          {/* Loading Indicator Pill */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
            padding: '12px 24px', borderRadius: '50px', 
            background: isDark ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(16px)', 
            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
            boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 10px 30px rgba(0,0,0,0.08)',
            width: 'fit-content', margin: '0 auto', marginBottom: '10px',
            direction: isRTL ? 'rtl' : 'ltr'
          }}>
            <Loader size={20} color="#2e7cba" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontWeight: '600', fontSize: '15px', color: isDark ? '#f1f5f9' : '#334155', animation: 'pulseText 2s infinite ease-in-out' }}>
              {creating ? t.creatingPayroll : t.loadingSalary}
            </span>
          </div>

          {/* Header Skeleton */}
          <div style={{ ...styles.card1, padding: '24px' }}>
            <div className="skeleton-box" style={{ width: '40%', height: '32px', marginBottom: '12px' }} />
            <div className="skeleton-box" style={{ width: '60%', height: '20px' }} />
          </div>

          {/* Filter Bar Skeleton */}
          <div style={{ ...styles.card1, padding: '20px', display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="skeleton-box" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
            <div className="skeleton-box" style={{ width: '160px', height: '42px', borderRadius: '10px' }} />
            <div className="skeleton-box" style={{ width: '160px', height: '42px', borderRadius: '10px' }} />
          </div>

          {/* Stats Grid Skeleton */}
          <div style={{ display: 'grid', gridTemplateColumns: screenSize.isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ ...styles.card1, padding: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div className="skeleton-box" style={{ width: '56px', height: '56px', borderRadius: '14px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="skeleton-box" style={{ width: '100%', height: '26px', marginBottom: '10px' }} />
                  <div className="skeleton-box" style={{ width: '60%', height: '18px' }} />
                </div>
              </div>
            ))}
          </div>

          {/* Main Sections Skeleton */}
          <div style={{ ...styles.card1, padding: '30px' }}>
            <div className="skeleton-box" style={{ width: '200px', height: '30px', marginBottom: '24px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[1, 2, 3].map(i => (
                <div key={i} style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <div className="skeleton-box" style={{ flex: 1, height: '60px', borderRadius: '12px' }} />
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    );
  }

  if (error === MISSING_SALARY_DATA) {
    return <MissingSalaryState t={t} theme={theme} isRTL={isRTL} isMobile={screenSize.isMobile} styles={styles} />;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        <div style={styles.mainContent}>
          <div style={styles.errorBox}>
            <p style={styles.errorText}>{t.errorTitle}: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!calculatedValues) {
    return (
      <div style={styles.container}>
        <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>
        <div style={styles.mainContent}>
          <div style={styles.emptyBox}><p style={styles.loadingText}>{t.noSalaryData}</p></div>
        </div>
      </div>
    );
  }

  // ── main render ───────────────────────────────────────────────
  return (
    <div style={{...styles.container, marginTop: "-30px", marginBottom: "20px"}}>
      <div style={styles.backgroundElements}><div style={styles.circle1} /><div style={styles.circle2} /></div>

      <div >

        {/* Header */}
        <div style={{ ...styles.card1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          <div>
            <h1 className="nx-page-title" style={styles.headerTitle}>{t.pageTitle}</h1>
            <p style={styles.headerSubtitle}>{t.pageSubtitle}</p>
          </div>
          {payrollData && (
            <span
              style={{
                flexShrink: 0,
                padding: '6px 14px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: '700',
                backgroundColor: payrollData.paymentStatus === 'paid' ? '#d1fae5' : '#fef3c7',
                color: payrollData.paymentStatus === 'paid' ? '#065f46' : '#92400e',
                whiteSpace: 'nowrap',
              }}
            >
              {payrollData.paymentStatus === 'paid' ? t.paymentStatusPaid : t.paymentStatusUnpaid}
            </span>
          )}
        </div>

        {/* Filter Bar */}
        <div style={styles.card1} className="no-print">
          <div style={styles.filterRow}>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.filterLabel}>{t.selectYear}:</span>
              <div style={{ minWidth: '120px' }}>
                <ReusableDropdown
                  items={Array.from(new Set([...availableYears, selectedYear])).sort((a, b) => b - a).map((y) => ({ id: y.toString(), label: y.toString() }))}
                  value={selectedYear.toString()}
                  onChange={(val) => { const n = Number(val); if (!isNaN(n)) setSelectedYear(n); }}
                  onSelect={(item) => setSelectedYear(Number(item.id))}
                  placeholder={t.selectYear}
                  theme={theme} language={language}
                  maxItems={10} showAllOnArrowClick showClear={false}
                  getItemKey={(i) => i.id} getItemLabel={(i) => i.label}
                  inputStyle={{ padding: '8px 40px 8px 16px', minWidth: '120px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={styles.filterLabel}>{t.selectMonth}:</span>
              <div style={{ minWidth: '150px' }}>
                <ReusableDropdown
                  items={Array.from(new Set([...availableMonths, selectedMonth])).sort((a, b) => a - b).map((m) => ({ id: m.toString(), label: getMonthName(m, t) }))}
                  value={selectedMonth.toString()}
                  onChange={(val) => { const n = Number(val); if (!isNaN(n)) setSelectedMonth(n); }}
                  onSelect={(item) => setSelectedMonth(Number(item.id))}
                  placeholder={t.selectMonth}
                  theme={theme} language={language}
                  maxItems={12} showAllOnArrowClick showClear={false}
                  getItemKey={(i) => i.id} getItemLabel={(i) => i.label}
                  inputStyle={{ padding: '8px 40px 8px 16px', minWidth: '150px' }}
                />
              </div>
            </div>

            <button
              onClick={handleDownloadPDF}
              style={styles.printButton}
              //onMouseLeave={(e) => { e.currentTarget.style.background = '#2e7cba'; e.currentTarget.style.transform = 'translateY(0)'; }}
              //onMouseEnter={(e) => { e.currentTarget.style.background = '#256ba1'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            >
              <Printer size={16} />
              {t.printPayslip}
            </button>
          </div>
        </div>

        {/* Stats */}
        <StatsGrid styles={statsGridStyles} stats={stats} screenSize={screenSize} />

        {/* Sections */}
        <DeductionsSection payrollData={payrollData} calculatedValues={calculatedValues} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
        <AllowancesSection payrollData={payrollData} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />
        <SalaryComposition payrollData={payrollData} calculatedValues={calculatedValues} styles={styles} t={t} theme={theme} isRTL={isRTL} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} />

        {/* Allowance Instances Table — hidden entirely when there are none */}
        {Array.isArray(payrollData.allowances) && payrollData.allowances.length > 0 && (
          <Mo3tiCard
            title={t.allowanceInstances}
            icon={TrendingUp}
            theme={theme}
            isRTL={isRTL}
          >
            <InstancesTable
              rows={payrollData.allowances}
              theme={theme}
              isRTL={isRTL}
              t={t}
              amountColor="#16a34a"
              amountSign="+"
            />
          </Mo3tiCard>
        )}

        {/* Deduction Instances Table */}
        <Mo3tiCard
          title={t.deductionInstances}
          icon={Calculator}
          theme={theme}
          isRTL={isRTL}
        >
          <InstancesTable
            rows={Array.isArray(payrollData.deductions) ? payrollData.deductions : []}
            theme={theme}
            isRTL={isRTL}
            t={t}
            emptyText={t.noDeductionsRow}
            amountColor="#dc2626"
            amountSign="-"
          />
        </Mo3tiCard>
      </div>

      {/* Hidden printable payslip */}
      {payrollData && (
        <PrintablePayslip payrollData={payrollData} employee={{ infinityCode, clientCode, arabicName }} theme={theme} t={t} isRTL={isRTL} selectedMonth={selectedMonth} selectedYear={selectedYear} calculatedValues={calculatedValues} />
      )}

      {/* Global CSS */}
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .hover-card { cursor: pointer; }
        .hover-card:hover {
          transform: ${screenSize.isMobile ? 'scale(1.02)' : 'translateY(-2px)'};
          box-shadow: ${theme === 'dark' ? '0 16px 48px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 16px 48px rgba(0,0,0,0.15)'} !important;
          background: ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.95)'} !important;
          border: ${theme === 'dark' ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(0,0,0,0.08)'} !important;
        }
        *::-webkit-scrollbar { width: ${screenSize.isMobile ? '4px' : '6px'}; }
        *::-webkit-scrollbar-track { background: ${theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}; border-radius: 3px; }
        *::-webkit-scrollbar-thumb { background: ${theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}; border-radius: 3px; }
        * { -webkit-tap-highlight-color: transparent; box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
      `}</style>

      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} variant={alertModal.variant} onClose={closeAlert} theme={theme === 'dark' ? 'dark' : 'light'} language={language} />
    </div>
  );
};

export default EgyptianSalaryPage;